/**
 * Content Filter Utility
 * 
 * Client-side content scanning for job postings.
 * Mirrors the word lists and logic from the auto-approve-jobs Edge Function
 * to enable immediate content scanning at job creation time.
 * 
 * When a free-tier employer posts a job, this scanner runs immediately
 * so flagged content is routed to "Evaluation Needed" instead of sitting
 * in the "Pending" queue unscanned for 48 hours.
 */

// ============================================================================
// OBSCENE / INAPPROPRIATE WORD LIST
// ============================================================================
// Keep in sync with supabase/functions/auto-approve-jobs/index.ts

const OBSCENE_WORDS = [
  // Profanity
  'fuck', 'shit', 'damn', 'bastard', 'bitch', 'asshole', 'dickhead',
  'motherfucker', 'bullshit', 'crap', 'piss', 'cock', 'cunt',
  // Slurs & hate speech
  'nigger', 'nigga', 'faggot', 'retard', 'retarded', 'spic', 'chink',
  'wetback', 'kike', 'gook', 'tranny', 'dyke',
  // Sexual content
  'porn', 'pornography', 'xxx', 'nude', 'nudes', 'naked', 'sexting',
  'escort', 'prostitute', 'hooker', 'stripper', 'onlyfans', 'cam girl',
  'cam boy', 'sex worker', 'erotic', 'fetish', 'nsfw',
  // Violence
  'kill', 'murder', 'torture', 'rape', 'assault', 'bomb', 'terrorist',
  // Drugs / illegal
  'cocaine', 'heroin', 'meth', 'weed dealer', 'drug dealer',
  'money laundering', 'counterfeit',
  // Scam indicators
  'get rich quick', 'pyramid scheme', 'mlm scheme', 'ponzi',
  'nigerian prince', 'wire transfer scam', 'advance fee',
];

// ============================================================================
// SPAM INDICATOR PHRASES
// ============================================================================

const SPAM_INDICATORS = [
  'bitcoin mining', 'crypto trading', 'forex signals',
  'make money fast', 'guaranteed income', 'unlimited earning',
  'no experience needed earn', 'click here now', 'act now limited',
  'wire money', 'send money western union', 'gift cards payment',
  'personal bank account', 'use your bank account',
  'dating site', 'adult content', 'adult entertainment',
  'gambling', 'casino', 'betting site', 'sports betting',
  'weight loss miracle', 'miracle cure', 'magic pill',
];

// ============================================================================
// VA RELEVANCE KEYWORDS
// ============================================================================

const VA_RELEVANCE_KEYWORDS = [
  // Core VA tasks
  'virtual assistant', 'administrative', 'data entry', 'email management',
  'calendar management', 'scheduling', 'bookkeeping', 'customer service',
  'customer support', 'social media', 'content creation', 'research',
  'transcription', 'project management', 'executive assistant',
  // Common VA skills
  'excel', 'spreadsheet', 'word processing', 'typing', 'filing',
  'microsoft office', 'google workspace', 'crm', 'invoicing',
  'phone calls', 'correspondence', 'proofreading', 'editing',
  // Remote work terms
  'remote', 'work from home', 'freelance', 'part-time', 'full-time',
  'contract', 'hourly', 'flexible hours', 'online',
  // Business tasks
  'marketing', 'sales support', 'lead generation', 'outreach',
  'recruiting', 'hr support', 'accounting', 'travel booking',
  'event planning', 'graphic design', 'web development', 'seo',
  'copywriting', 'blog', 'e-commerce', 'shopify', 'amazon',
  'real estate', 'property management', 'insurance', 'legal',
  'medical', 'healthcare', 'education', 'tutoring',
  // General business
  'business', 'company', 'office', 'team', 'manager', 'assistant',
  'coordinator', 'specialist', 'analyst', 'consultant', 'support',
  'operations', 'logistics', 'communications', 'client',
];

// ============================================================================
// PRE-COMPILED PATTERNS
// ============================================================================
// Patterns are compiled once at module load time — not on every scan call.

const OBSCENE_WORD_PATTERNS = OBSCENE_WORDS
  .filter((word) => !word.includes(' '))
  .map((word) => ({
    word,
    regex: new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i'),
  }));

const OBSCENE_PHRASES = OBSCENE_WORDS
  .filter((word) => word.includes(' '))
  .map((word) => word.toLowerCase());

// ============================================================================
// SCANNING FUNCTIONS
// ============================================================================

/**
 * Scans text for obscene words using word-boundary matching.
 * @param {string} text - Text to scan
 * @returns {string[]} Array of matched obscene words
 */
function scanForObsceneWords(text) {
  if (!text) return [];
  const lowerText = text.toLowerCase();
  const found = [];

  for (const phrase of OBSCENE_PHRASES) {
    if (lowerText.includes(phrase)) {
      found.push(phrase);
    }
  }

  for (const { word, regex } of OBSCENE_WORD_PATTERNS) {
    if (regex.test(lowerText)) {
      found.push(word);
    }
  }

  return [...new Set(found)];
}

/**
 * Scans for spam indicator phrases.
 * @param {string} text - Text to scan
 * @returns {string[]} Array of matched spam indicators
 */
function scanForSpamIndicators(text) {
  if (!text) return [];
  const lowerText = text.toLowerCase();
  const found = [];

  for (const indicator of SPAM_INDICATORS) {
    if (lowerText.includes(indicator.toLowerCase())) {
      found.push(indicator);
    }
  }

  // Excessive caps (more than 40% uppercase in text longer than 50 chars)
  if (text.length > 50) {
    const upperCount = (text.match(/[A-Z]/g) || []).length;
    const letterCount = (text.match(/[a-zA-Z]/g) || []).length;
    if (letterCount > 0 && (upperCount / letterCount) > 0.4) {
      found.push('Excessive uppercase text (possible spam)');
    }
  }

  // Excessive URLs (more than 3)
  const urlCount = (text.match(/https?:\/\/[^\s]+/gi) || []).length;
  if (urlCount > 3) {
    found.push(`Excessive URLs detected (${urlCount} links)`);
  }

  // Suspicious email patterns (multiple different email domains)
  const emails = text.match(/[\w.-]+@[\w.-]+\.\w+/gi) || [];
  const uniqueDomains = new Set(emails.map(e => e.split('@')[1]?.toLowerCase()));
  if (uniqueDomains.size > 2) {
    found.push('Multiple email domains detected (possible phishing)');
  }

  return [...new Set(found)];
}

/**
 * Checks if the job content is relevant to VA/remote work.
 * @param {string} title - Job title
 * @param {string} description - Job description
 * @param {string[]|null} skills - Skills array
 * @param {string|null} category - Job category
 * @returns {string[]} Array of irrelevance reasons (empty = relevant)
 */
function checkRelevance(title, description, skills, category) {
  const issues = [];
  const combinedText = `${title || ''} ${description || ''} ${(skills || []).join(' ')} ${category || ''}`.toLowerCase();

  let relevanceHits = 0;
  for (const keyword of VA_RELEVANCE_KEYWORDS) {
    if (combinedText.includes(keyword.toLowerCase())) {
      relevanceHits++;
    }
  }

  if (relevanceHits < 2) {
    issues.push(`Low relevance to VA work (only ${relevanceHits} relevant keyword(s) found)`);
  }

  if (title && title.trim().length < 5) {
    issues.push('Job title is suspiciously short');
  }

  if (description && description.trim().length < 30) {
    issues.push('Job description is too short for a legitimate posting');
  }

  if (!description || description.trim().length === 0) {
    issues.push('Job description is empty');
  }

  return issues;
}

// ============================================================================
// MAIN SCAN FUNCTION
// ============================================================================

/**
 * Scans a job posting's content for obscene words, spam, and relevance issues.
 * Returns a structured result indicating whether the content passed or failed.
 * 
 * @param {Object} job - Job data object
 * @param {string} job.title - Job title
 * @param {string} job.description - Job description
 * @param {string} [job.requirements] - Job requirements
 * @param {string} [job.qualifications] - Job qualifications
 * @param {string[]} [job.skills_required] - Skills array
 * @param {string} [job.category] - Job category
 * @returns {{ passed: boolean, flags: Object, reason: string }}
 */
export function scanJobContent(job) {
  const allText = [
    job.title,
    job.description,
    job.requirements,
    job.qualifications,
    (job.skills_required || []).join(' ')
  ].filter(Boolean).join(' ');

  const obsceneWords = scanForObsceneWords(allText);
  const spamIndicators = scanForSpamIndicators(allText);
  const irrelevantContent = checkRelevance(
    job.title,
    job.description,
    job.skills_required,
    job.category
  );

  const totalFlags = obsceneWords.length + spamIndicators.length + irrelevantContent.length;
  const passed = totalFlags === 0;

  const reasons = [];
  if (obsceneWords.length > 0) {
    reasons.push(`Obscene/inappropriate language detected: ${obsceneWords.join(', ')}`);
  }
  if (spamIndicators.length > 0) {
    reasons.push(`Spam indicators found: ${spamIndicators.join(', ')}`);
  }
  if (irrelevantContent.length > 0) {
    reasons.push(`Relevance issues: ${irrelevantContent.join('; ')}`);
  }

  return {
    passed,
    flags: {
      obscene_words: obsceneWords,
      spam_indicators: spamIndicators,
      irrelevant_content: irrelevantContent,
      scanned_at: new Date().toISOString(),
      total_flags: totalFlags,
    },
    reason: passed
      ? 'Content scan passed - no issues detected'
      : `Content flagged: ${reasons.join('. ')}`,
  };
}

